import { Dispatch, MouseEventHandler, SetStateAction } from 'react';
import { Image } from '../../commonTypes/image';
import { CallToAction } from '../../commonTypes/links';
import { IconSize } from '../Icon/styles';

export type PrimaryNav = {
  navItems: PrimaryNavItem[];
};

export type PrimaryNavProps = {
  primaryNav?: PrimaryNav;
  resetNav: () => void;
  activeItemIndex: number | null;
  setActiveItemIndex: Dispatch<SetStateAction<number | null>>;
  useFourColumnLayout?: boolean;
  showArrowsOnPrimaryNav?: boolean;
  dummyColumSectionAtEnd?: boolean;
};

export type SecondaryNav = {
  navItems: SecondaryNavItem[];
};

export type SimpleSecondaryNav = {
  navItems: SimpleSecondaryNavItem[];
};
export type SecondaryNavProps = {
  secondaryNav?: SecondaryNav;
};

export type SimpleSecondaryNavProps = {
  simpleSecondaryNav?: SimpleSecondaryNav;
};

export type HeaderLink = {
  name: string;
  url: string;
  screenReaderText?: string;
  openInNewTab: boolean;
  onClick?: MouseEventHandler;
};

export type TertiaryNavItemWithIcon = {
  icon: {
    name: string;
    size?: IconSize;
  };
} & HeaderLink;

export type TertiaryNavItem = HeaderLink;

export type PrimaryLink = HeaderLink;

export type PrimaryNavItem = SimplePrimaryNavItem | NestedPrimaryNavItem | PrimaryLink;

export type SimplePrimaryNavItem = NavSection & {
  ctas: CallToAction[];
  usp?: USP;
};

// Note that the following types have names different to the keys used
// because of backward compatibility reasons.
export type NestedPrimaryNavItem = {
  name: string;
  primarySection: NestedSection;
  onExpand?: MouseEventHandler;
  onCollapse?: MouseEventHandler;
};

export type SecondaryNavItem = NavSection & {
  icon?: string;
  primaryCta?: CallToAction;
  secondaryCta?: CallToAction;
};

export type SimpleSecondaryNavItem = {
  icon?: string;
  url: string;
  name: string;
  onClick?: MouseEventHandler;
};

export type USP = {
  image?: Image;
  header?: string;
  body: string;
};

export type NestedSection = {
  heading: string;
  subsections: NavSectionWithCtas[];
};

export type NavSectionWithCtas = NavSection & {
  ctas: CallToAction[];
};

export type NavSection = {
  name: string;
  sections: (LinkSection | CardSection)[];
  onExpand?: MouseEventHandler;
  onCollapse?: MouseEventHandler;
};

export type LinkSection = {
  heading?: string;
  links: NavLink[];
};

export type CardSection = {
  heading: string;
  text: string;
  cta?: CallToAction;
};

export type NavLink = {
  text: string;
  url: string;
  onClick?: MouseEventHandler;
};

/* Differentiator function for discerning difference between a simple nav item and a
nested nav item so that they can be handled differently. */
export const isSimplePrimaryNavItem = (
  navItem: SimplePrimaryNavItem | NestedPrimaryNavItem | PrimaryLink
): navItem is SimplePrimaryNavItem =>
  (navItem as SimplePrimaryNavItem).sections !== undefined;

/* Differentiator function for discerning difference between a primary link nav item and 
a simple nav item or nested nav item so that they can be handled differently. */
export const isPrimaryLinkNavItem = (
  navItem: SimplePrimaryNavItem | NestedPrimaryNavItem | PrimaryLink
): navItem is PrimaryLink => (navItem as PrimaryLink).url !== undefined;

/* Differentiator function for discerning difference between a tertiary link nav item and 
a tertiary link nav item with icon so that they can be handled differently. */
export const isTertiaryNavItemWithIcon = (
  navItem: TertiaryNavItemWithIcon | TertiaryNavItem
): navItem is TertiaryNavItemWithIcon =>
  (navItem as TertiaryNavItemWithIcon).icon !== undefined;

export const isCardSection = (
  section: LinkSection | CardSection
): section is CardSection => (section as CardSection).text !== undefined;

export type IsActiveProps = {
  isActive: boolean;
};
